import { isEmpty, mapValues } from 'lodash';
import { set } from 'dot-prop-immutable';
import { getLocalizedPropValue } from '../util/localization';

const chainedSet = (obj, ...args) => {
  const result = set(obj, ...args);

  return {
    result,
    set: chainedSet.bind(this, result),
  };
};

export const getOnboardingState = (store) => {
  const newOnboarding = chainedSet(
    store.onboarding,
    'degreeChoices',
    mapValues(store.onboarding.degreeChoices, (degreeItem) => {
      if (degreeItem === '') return ''; // '' is a special case for when the degree is not yet chosen

      return {
        ...degreeItem,
        // for now, several translated values of degree properties in the current language will be assigned to the 'En' property
        titleEn: getLocalizedPropValue(
          degreeItem,
          'title',
          store.currentLocale
        ),
        descriptionEn: getLocalizedPropValue(
          degreeItem,
          'description',
          store.currentLocale
        ),
        academicProgramType: getLocalizedPropValue(
          degreeItem,
          'academicProgramType',
          store.currentLocale
        ),
        degreeTypeEn: getLocalizedPropValue(
          degreeItem,
          'degreeType',
          store.currentLocale
        ),
        firstMathReq: getLocalizedPropValue(
          degreeItem,
          'firstMathReq',
          store.currentLocale
        ),
      };
    })
  )
    .set(
      'userData.degreeCareerIdMap',
      mapValues(store.onboarding.userData.degreeCareerIdMap, (value) => {
        return value.map((item) => ({
          ...item,
          // for now, several translated values of degree properties in the current language will be assigned to the 'En' property
          titleEn: getLocalizedPropValue(item, 'title', store.currentLocale),
          descriptionEn: getLocalizedPropValue(
            item,
            'description',
            store.currentLocale
          ),
          academicProgramType: getLocalizedPropValue(
            item,
            'academicProgramType',
            store.currentLocale
          ),
          degreeTypeEn: getLocalizedPropValue(
            item,
            'degreeType',
            store.currentLocale
          ),
          firstMathReq: getLocalizedPropValue(
            item,
            'firstMathReq',
            store.currentLocale
          ),
        }));
      })
    )
    .set(
      'careerChoices',
      mapValues(store.onboarding.careerChoices, (career) => {
        if (!career) return career;

        return {
          ...career,
          // for now, 'titleEn' will actually contain the translated title in the current language
          titleEn: getLocalizedPropValue(career, 'title', store.currentLocale),
          description: getLocalizedPropValue(
            career,
            'description',
            store.currentLocale
          ),
          tasks: getLocalizedPropValue(career, 'tasks', store.currentLocale),
        };
      })
    )
    .set(
      'favorites',
      store.onboarding.favorites.map((favorite) => ({
        ...favorite,
        // Translating properties for each item in the 'favorites' array
        categorytitleEn: getLocalizedPropValue(
          favorite,
          'categorytitle',
          store.currentLocale
        ),
        titleEn: getLocalizedPropValue(favorite, 'title', store.currentLocale),
        description: getLocalizedPropValue(
          favorite,
          'description',
          store.currentLocale
        ),
        tasksEn: getLocalizedPropValue(favorite, 'tasks', store.currentLocale),
        // Add additional properties to translate as needed
      }))
    )
    .set(
      'userData.careerGameResults',
      store.onboarding.userData.careerGameResults.map((career) => ({
        ...career,
        // for now, 'titleEn' will actually contain the translated title in the current language
        titleEn: getLocalizedPropValue(career, 'title', store.currentLocale),
        description: getLocalizedPropValue(
          career,
          'description',
          store.currentLocale
        ),
        tasks: getLocalizedPropValue(career, 'tasks', store.currentLocale),
      }))
    )
    .set(
      'userData.relatedCareers',
      store.onboarding.userData.relatedCareers?.map((career) => ({
        ...career,
        // for now, 'titleEn' will actually contain the translated title in the current language
        titleEn: getLocalizedPropValue(career, 'title', store.currentLocale),
        description: getLocalizedPropValue(
          career,
          'description',
          store.currentLocale
        ),
        tasks: getLocalizedPropValue(career, 'tasks', store.currentLocale),
      }))
    ).result;

  return newOnboarding;
};

export const getPreRegistrationState = (store) => {
  let topThree = store.preregistration.topThreeMatches;
  if (!(topThree instanceof Array)) {
    topThree = [];
  }
  const preregistration = chainedSet(
    store.preregistration,
    'imagePairs',
    store.preregistration?.imagePairs?.map((pair) =>
      mapValues(pair, (image) => ({
        ...image,
        description: getLocalizedPropValue(
          image,
          'description',
          store.currentLocale
        ),
      }))
    )
  ).set(
    'topThreeMatches',
    topThree.map((career) => ({
      ...career,
      // for now, 'titleEn' will actually contain the translated title in the current language
      titleEn: getLocalizedPropValue(career, 'title', store.currentLocale),
    }))
  ).result;

  return preregistration;
};

export const getAuthState = (store) => store.auth;

export const getUserGameInfo = (store) => store.userGameInfo;

export const getTopCareers = (store) => store.topCareers;

export const getTenantState = (store) => {

  // select translated button labels based on currentLocale
  const tenant = chainedSet(
    store.tenant,
    'buttonInfo',
    mapValues(store.tenant.buttonInfo, (info) => {
      if (!info) return info;

      if (info.isDropdown) {
        return {
          ...info,
          dropdownLabel: getLocalizedPropValue(
            info,
            'dropdownLabel',
            store.currentLocale
          ),
          childButtons: info.childButtons.map((button) => ({
            ...button,
            label: getLocalizedPropValue(button, 'label', store.currentLocale),
          })),
        };
      }

      return {
        ...info,
        label: getLocalizedPropValue(info, 'label', store.currentLocale),
      };
    })
  ).set(
    'allTenantCareers',
    store.tenant.allTenantCareers.map((career) => ({
      ...career,
      // for now, 'titleEn' will actually contain the translated title in the current language
      titleEn: getLocalizedPropValue(career, 'title', store.currentLocale),
      description: getLocalizedPropValue(
        career,
        'description',
        store.currentLocale
      ),
      tasks: getLocalizedPropValue(career, 'tasks', store.currentLocale),
    }))
  ).result;

  return tenant;
};

export const getEcapState = (store) => {
  // use translated title and outcomes for matched ecap HS courses
  const ecap = chainedSet(
    store.ecap,
    'plan.classes_matched',
    mapValues(store.ecap.plan.classes_matched, (courses) =>
      courses.map((course) => {
        if (course !== null) {
          return {
            ...course,
            requirement_title: getLocalizedPropValue(
              course,
              'title',
              store.currentLocale
            ),
            learning_outcomes: getLocalizedPropValue(
              course,
              'learningOutcomes',
              store.currentLocale
            ),
          };
        } else {
          return course;
        }
      })
    )
  ).result;

  return ecap;
};

export const getAdminState = (store) => store.admin;

export const getSkillsState = (store) => {
  // select translated skills data based on currentLocale
  const skills = chainedSet(
    store.skills,
    'skills',
    mapValues(store.skills.skills, (skill) => ({
      ...skill,
      skill: getLocalizedPropValue(skill, 'skill', store.currentLocale),
      description: getLocalizedPropValue(
        skill,
        'description',
        store.currentLocale
      ),
    }))
  ).set(
    'skillsCourses',
    store.skills.skillsCourses && {
      skillsCourses: store.skills.skillsCourses.skillsCourses?.map(
        (course) => ({
          ...course,
          upskilling_offering: getLocalizedPropValue(
            course,
            'upskillingOffering',
            store.currentLocale
          ),
          description: getLocalizedPropValue(
            course,
            'description',
            store.currentLocale
          ),
          offering_type: getLocalizedPropValue(
            course,
            'offeringType',
            store.currentLocale
          ),
        })
      ),
    }
  ).result;

  return skills;
};

export const getTenantSSOFlag = (state) => {
  const tenant = getTenantState(state);

  return tenant.features.includes('has_sso');
};

export const getRogueTenantSSO = (state) => {
  const auth = getAuthState(state);

  return auth.isRogueTenantSSO;
};
export const getUserDashboardState = (store) => store.userDashboard;

export const getRegistrationSource = (state) => {
  const onboarding = getOnboardingState(state);

  return (
    onboarding &&
    onboarding.userData &&
    onboarding.userData.user &&
    onboarding.userData.user.regSource
  );
};

export const getCareerChoices = (state) => {
  const onboarding = getOnboardingState(state);

  return onboarding.careerChoices;
};

export const getCareerResults = (state) => {
  const onboarding = getOnboardingState(state);

  const userDataCareers = onboarding.userData.careerGameResults;

  return userDataCareers;
};

export const getPreRegistrationFlag = (state) => {
  const preregistration = getPreRegistrationState(state);

  return preregistration.preregistrationFlag;
};

export const getUserFavorites = (state) => {
  const onboarding = getOnboardingState(state);

  return onboarding.favorites;
};

export const getDegreeChoices = (state) => {
  const onboarding = getOnboardingState(state);

  return onboarding.degreeChoices;
};

export const getAuthUserId = (state) => {
  const onboarding = getOnboardingState(state);

  return (
    onboarding.userId?.id ||
    (!isEmpty(onboarding.userData.user) && onboarding.userData.user.id)
  );
};

export const getUserScore = (state) => {
  const onboarding = getOnboardingState(state);
  const preregistration = getPreRegistrationState(state);

  if (!isEmpty(preregistration.score)) return preregistration.score;

  if (!isEmpty(onboarding.careerChoices))
    return {
      r: onboarding.userData.user.gameRChoices,
      i: onboarding.userData.user.gameIChoices,
      a: onboarding.userData.user.gameAChoices,
      s: onboarding.userData.user.gameSChoices,
      e: onboarding.userData.user.gameEChoices,
      c: onboarding.userData.user.gameCChoices,
    };

  return {};
};

export const getUserEmail = (state) => {
  const auth = getAuthState(state);

  return auth.user && auth.user.attributes && auth.user.attributes.email;
};

export const getProviderName = (state) => {
  const auth = getAuthState(state);

  return auth.user && auth.user.attributes && auth.user.attributes.providerName;
};

export const getCognitoUsername = (state) => {
  const onboarding = getOnboardingState(state);

  return onboarding.cognitoUsername;
};

export const getUserTerritory = (state) => {
  const onboarding = getOnboardingState(state);
  const preregistration = getPreRegistrationState(state);

  return (
    preregistration.territory ||
    (onboarding.userData.user && onboarding.userData.user.state)
  );
};

export const getUserCountry = (state) => {
  const onboarding = getOnboardingState(state);
  const preregistration = getPreRegistrationState(state);

  return (
    preregistration.country ||
    (onboarding.userData.user && onboarding.userData.user.country)
  );
};

export const getRegisterOptionsValue = (state) => {
  const preregistration = getPreRegistrationState(state);

  return preregistration.registerOptionsValue;
};

export const getEducationLevelSelector = (state) => {
  const preregistration = getPreRegistrationState(state);
  const onboarding = getOnboardingState(state);

  const preRegistrationEducationLevel =
    preregistration &&
    preregistration.education &&
    preregistration.education.educationLevel
      ? preregistration.education.educationLevel
      : null;

  const userDataEducationLevel =
    onboarding &&
    onboarding.userData &&
    onboarding.userData.user &&
    onboarding.userData.user.educationLevel
      ? onboarding.userData.user.educationLevel
      : null;

  return preRegistrationEducationLevel || userDataEducationLevel;
};

export const showBrightCareersOnly = (state) => {
  const onboarding = getOnboardingState(state);

  return onboarding.showBrightOutlookCareersOnly;
};

export const getUserHasRegistered = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  return auth.isAuthenticated && !!onboarding.userId;
};

export const getUserProfilePhoto = (state) => {
  const userDashboard = getUserDashboardState(state);

  return userDashboard.profilePhotoURL;
};

export const showOnlineDegreesOnly = (state) => {
  const onboarding = getOnboardingState(state);
  const selectedVal = onboarding && onboarding.showOnlineDegreesOnly;
  const registrationSource = getRegistrationSource(state);

  if (selectedVal === false || selectedVal === true) {
    return selectedVal;
  }
  if (
    [
      'onlinedegrees',
      'edplus',
      'starbuckscap',
      'starbuckscapuk',
      'uber_driver',
      'uber_eats',
      'uber_family',
      'ubereats_family',
      'pearson',
      'instride',
    ].includes(registrationSource)
  ) {
    return true;
  }
  if (state.tenant.name === 'InStride') {
    return true;
  }
};
